'use client'

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { isSeasonActive, getFlightType } from '@/data/deals'
import { findOpenJawPairs } from '@/lib/openJaw'
import { groupByRoute } from '@/lib/groupDeals'
export { groupByRoute } from '@/lib/groupDeals'
import destinations from '@/data/destinations-client.json'

const DEALS_PER_PAGE = 12


function dealBadgeColor(deal) {
  const type = deal.deal_type
  if (type === 'error_fare' || type === 'exceptional') return 'red'
  if (type === 'deal') return 'orange'
  if (type === 'good_price') return 'green'
  if (type === 'award') return 'award'
  return 'none'
}


export default function useDealsShell({ deals, generatedAt, currentUser, airports, suggestedOrigin, serverTier, serverHomeAirports, accessToken }) {
  const [historyMap, setHistoryMap] = useState({})
  useEffect(() => {
    if (!currentUser) return
    const token = (() => {
      try { const s = localStorage.getItem('fairfares_session'); return s ? (JSON.parse(s)?.access_token ?? null) : null } catch { return null }
    })()
    if (!token) return
    const routes = [...new Set(deals.map((d) => `${d.origin}-${d.destination}`))]
    Promise.all(routes.map((route) =>
      fetch(`/api/history/${route}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json()).then((d) => [route, d.prices ?? []]).catch(() => [route, []])
    )).then((entries) => setHistoryMap(Object.fromEntries(entries)))
  }, [currentUser]) // eslint-disable-line react-hooks/exhaustive-deps

  const [origin, setOrigin] = useState(null)
  const [hasOriginPref, setHasOriginPref] = useState(false)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const originMounted = useRef(false)
  const skipNextOriginTransition = useRef(false)
  useEffect(() => {
    if (!originMounted.current) { originMounted.current = true; return }
    if (skipNextOriginTransition.current) { skipNextOriginTransition.current = false; return }
    setIsTransitioning(true)
    const t = setTimeout(() => setIsTransitioning(false), 150)
    return () => clearTimeout(t)
  }, [origin])

  // Restore user prefs from localStorage after mount to prevent hydration mismatch
  useEffect(() => {
    const savedOrigin = localStorage.getItem('fairfares_origin')
    if (savedOrigin && airports.includes(savedOrigin)) {
      skipNextOriginTransition.current = true
      setOrigin(savedOrigin)
      setHasOriginPref(true)
    }
    const savedStops = localStorage.getItem('ff_stops_filter')
    if (savedStops && ['all', 'direct', 'max1'].includes(savedStops)) setStopsFilter(savedStops)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Apply geo-IP suggested origin when API returns it (only if user has no saved pref)
  // Use a ref to avoid hydration mismatch — only runs after mount
  const geoApplied = useRef(false)
  useEffect(() => {
    if (geoApplied.current) return
    if (suggestedOrigin && !hasOriginPref && airports?.includes(suggestedOrigin)) {
      geoApplied.current = true
      skipNextOriginTransition.current = true
      setOrigin(suggestedOrigin)
    }
  }, [suggestedOrigin]) // eslint-disable-line react-hooks/exhaustive-deps

  const [profile, setProfile] = useState(null)
  const [profileLoaded, setProfileLoaded] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showPreferences, setShowPreferences] = useState(false)
  const [sortBy, setSortBy] = useState('score')
  const [regionFilter, setRegionFilter] = useState('all')
  const [dealTypeTab, setDealTypeTab] = useState('all')
  const [destFilter, setDestFilter] = useState([])
  const [weekendOnly, setWeekendOnly] = useState(false)
  const [stopsFilter, setStopsFilter] = useState('all')
  const [showAirportLock, setShowAirportLock] = useState(false)
  const [installPrompt, setInstallPrompt] = useState(null)
  const [showInstallBanner, setShowInstallBanner] = useState(false)
  const [isOffline, setIsOffline] = useState(false)
  const [showSubscribedBanner, setShowSubscribedBanner] = useState(false)
  const [lastVisit, setLastVisit] = useState(null)
  const [isSubscribed, setIsSubscribed] = useState(false)
  const [notifPermission, setNotifPermission] = useState('default')
  const [searchText, setSearchText] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [maxPriceFilter, setMaxPriceFilter] = useState('')
  const [filterAirports, setFilterAirports] = useState([])
  const [airlineFilter, setAirlineFilter] = useState([])
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [yourDealsOnly, setYourDealsOnly] = useState(false)
  const [selectedOriginAirports, setSelectedOriginAirports] = useState([])
  const [airlineSearch, setAirlineSearch] = useState('')
  const [airlineDropdownOpen, setAirlineDropdownOpen] = useState(false)
  const [visibleCount, setVisibleCount] = useState(DEALS_PER_PAGE)

  useEffect(() => {
    if (!currentUser) { setProfile(null); setProfileLoaded(true); return }
    const headers = accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
    fetch('/api/profile', { headers })
      .then(r => r.ok ? r.json() : null)
      .then(p => {
        if (!p) return
        setProfile(p)
        if (p?.preferredRegions?.length === 1) {
          setRegionFilter(p.preferredRegions[0])
        }
        if (p?.homeAirport && airports.includes(p.homeAirport)) {
          skipNextOriginTransition.current = true
          setOrigin(p.homeAirport)
          setHasOriginPref(true)
          localStorage.setItem('fairfares_origin', p.homeAirport)
        }
      })
      .finally(() => setProfileLoaded(true))
  }, [currentUser]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { const stored = localStorage.getItem('ff_last_visit'); if (stored) setLastVisit(Number(stored)); localStorage.setItem('ff_last_visit', String(Date.now())) }, [])
  useEffect(() => { setIsSubscribed(localStorage.getItem('ff_subscribed') === 'true') }, [])
  useEffect(() => { if (typeof Notification !== 'undefined') setNotifPermission(Notification.permission) }, [])

  useEffect(() => {
    if (!currentUser?.email) return
    const supaUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
    const supaKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
    if (!supaUrl?.startsWith('https://') || !supaKey) return
    fetch(`${supaUrl}/rest/v1/email_subscribers?email=eq.${encodeURIComponent(currentUser.email)}&status=eq.confirmed&select=id`,
      { headers: { apikey: supaKey, Authorization: `Bearer ${supaKey}`, Accept: 'application/json' } })
      .then((r) => r.json())
      .then((rows) => { if (Array.isArray(rows) && rows.length > 0) { setIsSubscribed(true); localStorage.setItem('ff_subscribed', 'true') } })
      .catch(() => {})
  }, [currentUser])

  useEffect(() => {
    const update = () => setIsOffline(!navigator.onLine)
    update()
    window.addEventListener('online', update)
    window.addEventListener('offline', update)
    return () => { window.removeEventListener('online', update); window.removeEventListener('offline', update) }
  }, [])

  useEffect(() => {
    const visits = parseInt(localStorage.getItem('ff_visits') || '0', 10) + 1
    localStorage.setItem('ff_visits', String(visits))
    const handler = (e) => {
      e.preventDefault(); setInstallPrompt(e)
      if (visits >= 3 && !localStorage.getItem('ff_pwa')) setShowInstallBanner(true)
    }
    window.addEventListener('beforeinstallprompt', handler)
    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('subscribed') === '1') {
      setShowSubscribedBanner(true)
      params.delete('subscribed')
      window.history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''))
    }
  }, [])

  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get('destination')
    if (p) setDestFilter(p.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean))
  }, [])

  useEffect(() => {
    const p = new URLSearchParams(window.location.search)
    if (p.get('q')) setSearchText(p.get('q'))
    if (p.get('from')) setDateFrom(p.get('from'))
    if (p.get('to')) setDateTo(p.get('to'))
    if (p.get('maxprice')) setMaxPriceFilter(p.get('maxprice'))
    if (p.get('airports')) setFilterAirports(p.get('airports').split(',').filter(Boolean))
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (searchText) params.set('q', searchText); else params.delete('q')
    if (dateFrom) params.set('from', dateFrom); else params.delete('from')
    if (dateTo) params.set('to', dateTo); else params.delete('to')
    if (maxPriceFilter) params.set('maxprice', maxPriceFilter); else params.delete('maxprice')
    if (filterAirports.length) params.set('airports', filterAirports.join(',')); else params.delete('airports')
    window.history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''))
  }, [searchText, dateFrom, dateTo, maxPriceFilter, filterAirports])


  useEffect(() => {
    setVisibleCount(DEALS_PER_PAGE)
  }, [origin, dealTypeTab, weekendOnly, stopsFilter, dateFrom, dateTo, maxPriceFilter, searchText, selectedOriginAirports]) // eslint-disable-line react-hooks/exhaustive-deps

  const updateDestFilter = useCallback((newFilter) => {
    setDestFilter(newFilter)
    const params = new URLSearchParams(window.location.search)
    if (newFilter.length === 0) params.delete('destination'); else params.set('destination', newFilter.join(','))
    window.history.replaceState(null, '', window.location.pathname + (params.toString() ? '?' + params.toString() : ''))
  }, [])

  function requestPushPermission() {
    if (typeof Notification === 'undefined') return
    Notification.requestPermission().then((perm) => {
      setNotifPermission(perm)
      window.plausible?.('ErrorFarePushNotif', { props: { outcome: perm } })
    })
  }

  const todayStr  = new Date().toISOString().slice(0, 10)
  const todayMs   = (() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), n.getDate()).getTime() })()
  const toLocalMs = (s) => { const [y, m, d] = s.split('-').map(Number); return new Date(y, m - 1, d).getTime() }
  const getNights = (deal) => {
    if (!deal.returnDate || !deal.departureDate) return null
    const [dy, dm, dd] = deal.departureDate.split('-').map(Number)
    const [ry, rm, rd] = deal.returnDate.split('-').map(Number)
    return Math.round((new Date(ry, rm - 1, rd) - new Date(dy, dm - 1, dd)) / 86400000)
  }

  const tier          = serverTier ?? profile?.tier ?? (profile?.premium ? 'plus' : 'free')
  const isPaid        = tier === 'plus' || tier === 'pro'
  const FLEX_CAP      = tier === 'pro' ? 7 : tier === 'plus' ? 3 : 0
  const AIRPORT_LIMIT = tier === 'pro' ? 15 : tier === 'plus' ? 5 : 1
  const flexDays      = Math.min(profile?.flexDays ?? 0, FLEX_CAP)
  const priceWatches  = Array.isArray(profile?.price_watches) ? profile.price_watches : []
  const homeAirports  = serverHomeAirports ?? (profile ? [...new Set([...(Array.isArray(profile.homeAirports) ? profile.homeAirports : []), ...(profile.homeAirport ? [profile.homeAirport] : [])])] : [])

  const dealMatchesPriceWatch = (deal) => !!priceWatches.length && priceWatches.some((w) =>
    w.origin === deal.origin && (w.destination === '*' || w.destination === deal.destination) && deal.price <= (w.max_price || 9999)
  )

  useEffect(() => {
    if (!priceWatches.length) { localStorage.removeItem('ff_pw_match_count'); return }
    const count = deals.filter((d) => priceWatches.some((w) =>
      w.origin === d.origin && (w.destination === '*' || w.destination === d.destination) && d.price <= (w.max_price || 9999)
    )).length
    localStorage.setItem('ff_pw_match_count', String(count))
  }, [priceWatches]) // eslint-disable-line react-hooks/exhaustive-deps

  const openJawPairs = useMemo(() => findOpenJawPairs(deals, { filterOrigin: origin }).slice(0, tier === 'pro' ? 10 : 5), [deals, origin, tier])

  const dealCounts     = airports.reduce((acc, ap) => { acc[ap] = deals.filter(d => d.origin === ap).length; return acc }, {})
  const liveDealCounts = airports.reduce((acc, ap) => { acc[ap] = deals.filter((d) => d.origin === ap && (d.departureDate ?? '') >= todayStr).length; return acc }, {})
  const allDestinations = [...new Set(deals.filter((d) => !origin || d.origin === origin).map((d) => d.destination))].sort()
  const allAirlines     = [...new Set(deals.map((d) => d.airline).filter(Boolean))].sort()
  const activeAdvancedFilters = [searchText.trim(), dateFrom, dateTo, maxPriceFilter, filterAirports.length > 0, airlineFilter.length > 0].filter(Boolean).length
  const clearAdvancedFilters  = () => { setSearchText(''); setDateFrom(''); setDateTo(''); setMaxPriceFilter(''); setFilterAirports([]); setAirlineFilter([]) }
  const filtersCount = activeAdvancedFilters + (stopsFilter !== 'all' ? 1 : 0) + (weekendOnly ? 1 : 0) + (selectedOriginAirports.length > 0 ? 1 : 0)

  const { filtered, annotated } = useMemo(() => {
    const filtered = deals
      .filter((d) => {
        if (dealTypeTab === 'all') return true
        if (d.deal_type === 'award') return dealTypeTab === 'award'
        return d.deal_type === dealTypeTab
      })
      .filter((d) => d._locked || d._featured || (!currentUser ? true : selectedOriginAirports.length > 0 ? selectedOriginAirports.includes(d.origin) : homeAirports.length > 0 ? true : (isPaid && filterAirports.length > 0 ? filterAirports.includes(d.origin) : !hasOriginPref ? true : d.origin === origin)))
      .filter((d) => destFilter.length === 0 || destFilter.includes(d.destination))
      .filter((d) => { if (!weekendOnly) return true; const n = getNights(d); return n != null && n >= 2 && n <= 3 })
      .filter((d) => isSeasonActive(d.seasons ?? 'year-round'))
      .filter((d) => stopsFilter === 'direct' ? (d.stops ?? 0) === 0 : stopsFilter === 'max1' ? (d.stops ?? 0) <= 1 : true)
      .filter((d) => !dateFrom || (d.departureDate ?? '') >= dateFrom)
      .filter((d) => !dateTo || (d.departureDate ?? '') <= dateTo)
      .filter((d) => !maxPriceFilter || d.price <= Number(maxPriceFilter))
      .filter((d) => { if (!searchText.trim()) return true; const q = searchText.trim().toLowerCase(); const cn = (destinations[d.destination]?.city_name ?? d.destination).toLowerCase(); return cn.includes(q) || d.destination.toLowerCase().includes(q) })
      .filter((d) => airlineFilter.length === 0 || airlineFilter.includes(d.airline))
      .filter((d) => regionFilter === 'all' || destinations[d.destination]?.region === regionFilter)

    const annotated = filtered.map((d, i) => {
      const days = d.departureDate ? (toLocalMs(d.departureDate) - todayMs) / 86400000 : Infinity
      return { ...d, created_at: d.created_at ?? generatedAt, isFlexMatch: flexDays > 0 && days >= 0 && days <= flexDays, isPriceWatchMatch: dealMatchesPriceWatch(d), _proOnly: tier !== 'pro' && getFlightType(d.destination) === 'long_haul', _idx: i }
    })
    return { filtered, annotated }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deals, dealTypeTab, currentUser, selectedOriginAirports, homeAirports, filterAirports, isPaid, hasOriginPref, origin, destFilter, weekendOnly, stopsFilter, dateFrom, dateTo, maxPriceFilter, searchText, airlineFilter, regionFilter, generatedAt, flexDays, tier, todayMs])

  const yourDeals = homeAirports.length > 0
    ? [...deals].filter((d) => homeAirports.includes(d.origin))
        .sort((a, b) => {
          const am = dealMatchesPriceWatch(a) ? 1 : 0; const bm = dealMatchesPriceWatch(b) ? 1 : 0
          if (am !== bm) return bm - am
          return (b.score ?? b.discountPct / 10) - (a.score ?? a.discountPct / 10)
        })
        .map((d) => { const days = d.departureDate ? (toLocalMs(d.departureDate) - todayMs) / 86400000 : Infinity; return { ...d, created_at: d.created_at ?? generatedAt, isFlexMatch: flexDays > 0 && days >= 0 && days <= flexDays, isPriceWatchMatch: dealMatchesPriceWatch(d) } })
        .slice(0, 5)
    : []

  const sorted = [...annotated].sort((a, b) => {
    if (sortBy === 'score') { const ae = a.deal_type === 'error_fare' ? 1 : 0; const be = b.deal_type === 'error_fare' ? 1 : 0; if (ae !== be) return be - ae }
    if (sortBy === 'price') return a.price - b.price
    if (sortBy === 'discount') return b.discountPct - a.discountPct
    if (sortBy === 'newest') return a._idx - b._idx
    return (b.score ?? b.discountPct / 10) - (a.score ?? a.discountPct / 10)
  })

  const displaySorted = groupByRoute(sorted)

  const FREE_PREVIEW = 25

  // DealOfDay: always from the 5 unlocked featured/curated picks
  const unlocked = displaySorted.filter(d => !d._locked)
  const dealOfDay  = unlocked.find(d => d._dealOfHour) ?? (unlocked.length > 0 ? [...unlocked].sort((a, b) => (b.score ?? b.discountPct / 10) - (a.score ?? a.discountPct / 10))[0] : null)
  const sortedMain = dealOfDay ? displaySorted.filter((d) => d.id !== dealOfDay.id) : displaySorted
  const hasLockedDeals = sortedMain.some(d => d._locked)
  const isGated    = !currentUser || hasLockedDeals
  const isFreeUser = !!currentUser && !isPaid

  const visible = (() => {
    if (isFreeUser) {
      // Free: unlocked first, fill up to FREE_PREVIEW with locked shells
      const unlocked = sortedMain.filter(d => !d._locked)
      const locked   = sortedMain.filter(d => d._locked)
      return [...unlocked, ...locked].slice(0, FREE_PREVIEW)
    }
    if (!isGated) return sortedMain.slice(0, isPaid ? visibleCount : sortedMain.length)
    return sortedMain.filter(d => !d._locked)
  })()
  const hidden  = isGated ? sortedMain.filter(d => d._locked).slice(isFreeUser ? FREE_PREVIEW : 0) : []
  const hasMore = !isGated && !isFreeUser && sortedMain.length > visibleCount

  const _dealGradients = (() => {
    const seen = new Set(); const map = new Map()
    const mark = (arr) => arr.forEach(d => { map.set(d.id, seen.has(d.destination)); seen.add(d.destination) })
    mark(visible); mark(hidden)
    return map
  })()

  // Deal counts per region (from all unfiltered deals — for region pill labels)
  const regionCounts = {}
  for (const d of deals) {
    const rgn = destinations[d.destination]?.region
    if (rgn) regionCounts[rgn] = (regionCounts[rgn] ?? 0) + 1
  }
  regionCounts.all = deals.length

  // For empty-state upsell: how many locked deals exist in the selected region
  const lockedInRegion = hidden.length

  const filterPanelProps = {
    stopsFilter, setStopsFilter, weekendOnly, setWeekendOnly, isPaid,
    dateFrom, setDateFrom, dateTo, setDateTo, maxPriceFilter, setMaxPriceFilter,
    allAirlines, airlineSearch, setAirlineSearch, airlineDropdownOpen, setAirlineDropdownOpen,
    airlineFilter, setAirlineFilter, airports, filterAirports, setFilterAirports,
  }

  return {
    historyMap, origin, setOrigin, hasOriginPref, setHasOriginPref, isTransitioning,
    profile, setProfile, profileLoaded, showModal, setShowModal, showPreferences, setShowPreferences,
    sortBy, setSortBy, dealTypeTab, setDealTypeTab,
    destFilter, updateDestFilter,
    weekendOnly, setWeekendOnly, stopsFilter, setStopsFilter,
    showAirportLock, setShowAirportLock,
    installPrompt, setInstallPrompt, showInstallBanner, setShowInstallBanner,
    isOffline, showSubscribedBanner, setShowSubscribedBanner,
    lastVisit, isSubscribed, notifPermission, requestPushPermission,
    searchText, setSearchText, dateFrom, setDateFrom, dateTo, setDateTo,
    maxPriceFilter, setMaxPriceFilter, filterAirports, setFilterAirports,
    airlineFilter, setAirlineFilter, filtersOpen, setFiltersOpen,
    airlineSearch, setAirlineSearch, airlineDropdownOpen, setAirlineDropdownOpen,
    visibleCount, setVisibleCount,
    dealCounts, liveDealCounts, allDestinations, allAirlines,
    activeAdvancedFilters, clearAdvancedFilters, filtersCount,
    tier, isPaid, FLEX_CAP, AIRPORT_LIMIT, flexDays, priceWatches, homeAirports, openJawPairs,
    annotated, yourDeals, sorted: displaySorted, filteredDealsCount: sorted.length, dealOfDay, sortedMain,
    isGated, visible, hidden, hasMore, _dealGradients, filterPanelProps,
    yourDealsOnly, setYourDealsOnly,
    selectedOriginAirports, setSelectedOriginAirports,
    regionFilter, setRegionFilter, regionCounts, lockedInRegion,
  }
}

